import { ExternalLink, FileText, Plus } from "lucide-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useChatContext } from "@/stores/chat-context-store";

interface DocumentCardData {
  id: string;
  fileName: string;
  summary?: string | null;
  processingStatus?: string | null;
  fileType?: string | null;
}

interface DocumentCardProps {
  document: DocumentCardData;
}

export function DocumentCard({ document }: DocumentCardProps) {
  const { addEntity } = useChatContext();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const handleClick = () => {
    void navigate({
      to: "/$workspaceSlug/documents",
      params: { workspaceSlug: workspaceSlug ?? "" },
      search: { doc: document.id },
    });
  };

  const handleAddToContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    addEntity({
      type: "document",
      id: document.id,
      label: document.fileName,
    });
  };

  return (
    <div className="relative flex items-start gap-3 w-full p-3 rounded-lg border border-border/60 bg-surface hover:bg-accent/10 hover:border-accent/30 transition-all text-left group cursor-pointer">
      <button
        type="button"
        onClick={handleClick}
        className="absolute inset-0 rounded-lg"
        aria-label={`Open ${document.fileName}`}
      />
      <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent/10">
        <FileText size={16} className="text-accent" />
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-primary truncate">
            {document.fileName}
          </span>
        </div>
        {document.summary && (
          <p className="text-xs text-muted mt-0.5 line-clamp-2">
            {document.summary}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {document.fileType && (
            <span className="text-[10px] font-medium text-muted uppercase tracking-wider bg-card rounded px-1.5 py-0.5">
              {document.fileType}
            </span>
          )}
          {document.processingStatus && (
            <span className="text-[10px] text-muted">
              {document.processingStatus}
            </span>
          )}
        </div>
      </div>
      <div className="relative flex flex-col items-center gap-1 shrink-0 self-start">
        <span className="flex items-center gap-0.5 text-[10px] text-muted opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink size={10} />
          Open
        </span>
        <button
          type="button"
          onClick={handleAddToContext}
          title="Add to chat context"
          className="z-10 flex items-center justify-center h-6 w-6 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent/10 text-muted hover:text-accent"
        >
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}
